<template>
  <div class="mobile-record-list" v-loading="loading">
    <div v-if="!loading && !rows.length" class="mobile-record-empty">
      <i class="el-icon-document"></i>
      <span>{{ emptyText }}</span>
    </div>

    <article
      v-for="(row, index) in rows"
      :key="getRowKey(row, index)"
      class="mobile-record-card"
      role="button"
      tabindex="0"
      @click="openDetail(row)"
      @keydown.enter.prevent="openDetail(row)"
    >
      <header class="mobile-record-card__header">
        <div class="mobile-record-card__identity">
          <strong>{{ resolveContent(title, row, index) || `记录 ${index + 1}` }}</strong>
          <span v-if="resolveContent(subtitle, row, index)">
            {{ resolveContent(subtitle, row, index) }}
          </span>
        </div>
        <i class="el-icon-arrow-right mobile-record-card__arrow"></i>
      </header>

      <dl class="mobile-record-card__fields" :style="summaryGridStyle">
        <div v-for="field in summaryFields" :key="field.label" class="mobile-record-field">
          <dt>{{ field.label }}</dt>
          <dd>
            <el-tag
              v-if="isTagField(field, row)"
              size="mini"
              :type="getTagType(field, row)"
            >
              {{ getFieldValue(field, row) }}
            </el-tag>
            <span v-else>{{ getFieldValue(field, row) }}</span>
          </dd>
        </div>
      </dl>

      <footer class="mobile-record-card__footer" @click.stop>
        <button type="button" class="mobile-record-detail-button" @click="openDetail(row)">
          <i class="el-icon-document"></i>
          全部字段
        </button>
        <responsive-actions
          v-if="getActions(row).length"
          :items="getActions(row)"
          @select="emitAction($event, row)"
        />
      </footer>
    </article>

    <el-drawer
      :visible.sync="detailVisible"
      direction="btt"
      size="86%"
      :append-to-body="true"
      :destroy-on-close="false"
      custom-class="mobile-record-drawer"
    >
      <div slot="title" class="mobile-record-drawer__title">
        <strong>{{ activeRow ? resolveContent(title, activeRow, 0) : "记录详情" }}</strong>
        <span v-if="activeRow && resolveContent(subtitle, activeRow, 0)">
          {{ resolveContent(subtitle, activeRow, 0) }}
        </span>
      </div>

      <dl v-if="activeRow" class="mobile-record-detail-list">
        <div v-for="field in detailFields" :key="field.label" class="mobile-record-detail-row">
          <dt>{{ field.label }}</dt>
          <dd>
            <el-tag
              v-if="isTagField(field, activeRow)"
              size="mini"
              :type="getTagType(field, activeRow)"
            >
              {{ getFieldValue(field, activeRow) }}
            </el-tag>
            <span v-else>{{ getFieldValue(field, activeRow) }}</span>
          </dd>
        </div>
      </dl>

      <div
        v-if="activeRow && getActions(activeRow).length"
        class="mobile-record-drawer__actions"
      >
        <responsive-actions
          :items="getActions(activeRow)"
          @select="emitAction($event, activeRow)"
        />
      </div>
    </el-drawer>
  </div>
</template>

<script>
function getValueByPath(target, path) {
  if (!path) return "";
  return String(path)
    .split(".")
    .reduce((value, key) => (value == null ? value : value[key]), target);
}

export default {
  name: "MobileRecordList",
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    fields: {
      type: Array,
      default: () => [],
    },
    title: {
      type: [String, Function],
      default: "id",
    },
    subtitle: {
      type: [String, Function],
      default: "",
    },
    actions: {
      type: [Array, Function],
      default: () => [],
    },
    rowKey: {
      type: [String, Function],
      default: "id",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: "暂无数据",
    },
    summaryLimit: {
      type: Number,
      default: 4,
    },
    summaryColumns: {
      type: Number,
      default: 2,
    },
  },
  data() {
    return {
      detailVisible: false,
      activeRow: null,
    };
  },
  computed: {
    summaryGridStyle() {
      const columns = Math.max(1, Math.min(4, Number(this.summaryColumns) || 2));
      return {
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
      };
    },
    summaryFields() {
      return this.fields
        .filter((field) => field && field.summary !== false)
        .slice(0, this.summaryLimit);
    },
    detailFields() {
      return this.fields.filter((field) => field && field.detail !== false);
    },
  },
  methods: {
    resolveContent(resolver, row, index) {
      if (typeof resolver === "function") return resolver(row, index);
      return getValueByPath(row, resolver);
    },
    getRowKey(row, index) {
      const value = this.resolveContent(this.rowKey, row, index);
      return value === "" || value == null ? index : value;
    },
    getFieldValue(field, row) {
      let value = field.value
        ? field.value(row)
        : getValueByPath(row, field.key);
      if (typeof field.formatter === "function") {
        value = field.formatter(value, row);
      }
      if (Array.isArray(value)) value = value.join(" / ");
      if (value === "" || value === null || value === undefined) return "--";
      if (typeof value === "object") return JSON.stringify(value);
      return String(value);
    },
    isTagField(field, row) {
      return typeof field.tag === "function" ? field.tag(row) : Boolean(field.tag);
    },
    getTagType(field, row) {
      return typeof field.tagType === "function"
        ? field.tagType(row)
        : field.tagType || "info";
    },
    getActions(row) {
      const actions = typeof this.actions === "function" ? this.actions(row) : this.actions;
      return Array.isArray(actions) ? actions : [];
    },
    openDetail(row) {
      this.activeRow = row;
      this.detailVisible = true;
      this.$emit("open", row);
    },
    emitAction(key, row) {
      this.detailVisible = false;
      this.$emit("action", key, row);
    },
  },
};
</script>

<style lang="less" scoped>
.mobile-record-list {
  display: grid;
  gap: 8px;
}

.mobile-record-empty {
  min-height: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #909399;

  i {
    font-size: 30px;
  }
}

.mobile-record-card {
  min-width: 0;
  padding: 12px;
  border: 1px solid #dfe7f2;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(33, 74, 133, 0.06);
  outline: none;
}

.mobile-record-card:focus-visible {
  border-color: #409eff;
  box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.14);
}

.mobile-record-card__header,
.mobile-record-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.mobile-record-card__identity {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;

  strong,
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: #203864;
    font-size: 15px;
  }

  span {
    color: #8492a6;
    font-size: 12px;
  }
}

.mobile-record-card__arrow {
  flex: 0 0 auto;
  color: #9aabc4;
}

.mobile-record-card__fields {
  margin: 10px 0 0;
  padding: 10px 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 14px;
  border-top: 1px solid #edf1f7;
  border-bottom: 1px solid #edf1f7;
}

.mobile-record-field {
  min-width: 0;

  dt {
    margin-bottom: 4px;
    color: #8492a6;
    font-size: 11px;
  }

  dd {
    min-width: 0;
    margin: 0;
    overflow: hidden;
    color: #303133;
    font-size: 13px;
    line-height: 1.4;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.mobile-record-card__footer {
  min-height: 44px;
  padding-top: 6px;
}

.mobile-record-detail-button {
  min-height: 44px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #3377d5;
  cursor: pointer;

  i {
    margin-right: 5px;
  }
}
</style>
